import { useEffect } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { langMeta, SUPPORTED_LANGS, DEFAULT_LANG, type Lang } from '@/i18n';
import { getOgImage } from '@/utils/ogImage';
import { getSeoKeywords, getSeoTagline } from '@/i18n/seoKeywords';
import { useSeoKeywordMapping } from '@/hooks/useSeoKeywordMapping';

interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  /** Extra page-specific keywords prepended to the locale's TOEFL keyword set. */
  keywords?: string[];
  /** Optional course/page-level JSON-LD type, e.g. 'Course', 'LearningResource'. */
  contentType?: 'WebPage' | 'Course' | 'LearningResource';
}

const SITE_URL = 'https://power-toefl.com';

/** Build the full URL for a given lang + base path (path without lang prefix).
 *  Root URL is canonicalized WITH trailing slash for ko/default to match
 *  sitemap and inject-static-meta. Non-root paths have no trailing slash. */
const langUrl = (basePath: string, lang: Lang): string => {
  const prefix = lang === DEFAULT_LANG ? '' : `/${lang}`;
  if (basePath === '/') {
    return prefix ? `${SITE_URL}${prefix}` : `${SITE_URL}/`;
  }
  return `${SITE_URL}${prefix}${basePath}`;
};

const SEOHead = ({ title, description, path, ogImage, keywords, contentType = 'WebPage' }: SEOHeadProps) => {
  const { lang } = useI18n();
  const disableMapping = /^\/test(?:\/|$)/.test(path);
  const mapping = useSeoKeywordMapping(path);
  // When a long-tail mapping exists for this URL+locale, use it as the
  // primary source. Caller-provided props remain the fallback.
  const effectiveTitle = (!disableMapping && mapping?.title_suggestion) || title;
  const effectiveDescription = (!disableMapping && mapping?.meta_description_suggestion) || description;
  const resolvedOgImage = ogImage ?? getOgImage(lang);
  const mappedKeywords = (!disableMapping && mapping)
    ? [mapping.primary_keyword, ...(mapping.secondary_keywords || [])]
    : undefined;
  const localizedKeywords = getSeoKeywords(lang, mappedKeywords ?? keywords);
  const tagline = getSeoTagline(lang);

  useEffect(() => {
    document.title = effectiveTitle;
    document.documentElement.lang = langMeta[lang].htmlLang;

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('name', 'description', effectiveDescription);
    // TOEFL-focused multilingual keyword sets — one of the strongest signals for long-tail search.
    setMeta('name', 'keywords', localizedKeywords);
    setMeta('name', 'news_keywords', localizedKeywords);
    setMeta('name', 'subject', `TOEFL — ${tagline}`);
    setMeta('name', 'classification', 'Education, Test Preparation, TOEFL');
    setMeta('name', 'audience', 'TOEFL test takers, students, ESL learners');
    setMeta('property', 'og:title', effectiveTitle);
    setMeta('property', 'og:description', effectiveDescription);
    setMeta('property', 'og:url', langUrl(path, lang));
    setMeta('property', 'og:image', `${SITE_URL}${resolvedOgImage}`);
    setMeta('property', 'og:image:alt', `${effectiveTitle} — Power TOEFL`);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('property', 'og:type', contentType === 'Course' ? 'article' : 'website');
    setMeta('property', 'og:locale', langMeta[lang].ogLocale);
    setMeta('property', 'og:site_name', 'Power TOEFL');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:site', '@PowerTOEFL');
    setMeta('name', 'twitter:title', effectiveTitle);
    setMeta('name', 'twitter:description', effectiveDescription);
    setMeta('name', 'twitter:image', `${SITE_URL}${resolvedOgImage}`);
    setMeta('name', 'twitter:image:alt', `${effectiveTitle} — Power TOEFL`);

    // --- og:locale:alternate for other languages ---
    document.querySelectorAll('meta[property="og:locale:alternate"]').forEach(el => el.remove());
    for (const l of SUPPORTED_LANGS) {
      if (l === lang) continue;
      const altMeta = document.createElement('meta');
      altMeta.setAttribute('property', 'og:locale:alternate');
      altMeta.setAttribute('content', langMeta[l].ogLocale);
      document.head.appendChild(altMeta);
    }

    // --- Canonical (self-referencing per language) ---
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    // path comes WITHOUT lang prefix — build canonical for current lang
    canonical.href = langUrl(path, lang);

    // path is always basePath (without lang prefix) — use directly for hreflang
    const basePath = path;

    // --- hreflang alternate links (ko, ja, th, vi + x-default) ---
    const hreflangKeys = [...SUPPORTED_LANGS.map(l => langMeta[l].hreflang), 'x-default'];

    // Remove stale hreflang links
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => {
      const hl = el.getAttribute('hreflang');
      if (!hl || hreflangKeys.includes(hl)) el.remove();
    });

    // Add fresh hreflang links
    for (const l of SUPPORTED_LANGS) {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = langMeta[l].hreflang;
      link.href = langUrl(basePath, l);
      document.head.appendChild(link);
    }

    // x-default → default language (ko)
    const xDefault = document.createElement('link');
    xDefault.rel = 'alternate';
    xDefault.hreflang = 'x-default';
    xDefault.href = langUrl(basePath, DEFAULT_LANG);
    document.head.appendChild(xDefault);

    // --- WebSite JSON-LD (homepage only to avoid duplication) ---
    const jsonLdId = 'seo-head-jsonld';
    const isHome = path === '/' || path === '';
    if (isHome) {
      let scriptEl = document.getElementById(jsonLdId);
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = jsonLdId;
        scriptEl.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebSite',
            '@id': `${SITE_URL}/#website`,
            name: 'Power TOEFL',
            url: SITE_URL,
            description: effectiveDescription,
            inLanguage: langMeta[lang].hreflang,
            keywords: localizedKeywords,
            publisher: { '@id': `${SITE_URL}/#org` },
            potentialAction: {
              '@type': 'SearchAction',
              target: `${SITE_URL}/grammar?q={search_term_string}`,
              'query-input': 'required name=search_term_string',
            },
          },
          {
            '@type': 'EducationalOrganization',
            '@id': `${SITE_URL}/#org`,
            name: 'Power TOEFL',
            url: SITE_URL,
            logo: `${SITE_URL}/favicon.png`,
            description: tagline,
            sameAs: [
              'https://twitter.com/PowerTOEFL',
              'https://www.facebook.com/PowerTOEFL',
            ],
          },
        ],
      });
    } else {
      // Remove WebSite JSON-LD if navigating away from home
      const existing = document.getElementById(jsonLdId);
      if (existing) existing.remove();
    }

    // --- Course JSON-LD (when contentType is Course/LearningResource) ---
    const courseLdId = 'seo-head-course-jsonld';
    if (contentType === 'Course' || contentType === 'LearningResource') {
      let courseEl = document.getElementById(courseLdId);
      if (!courseEl) {
        courseEl = document.createElement('script');
        courseEl.id = courseLdId;
        courseEl.setAttribute('type', 'application/ld+json');
        document.head.appendChild(courseEl);
      }
      courseEl.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': contentType,
        name: effectiveTitle,
        description: effectiveDescription,
        url: langUrl(path, lang),
        inLanguage: langMeta[lang].hreflang,
        keywords: localizedKeywords,
        educationalLevel: 'TOEFL iBT',
        learningResourceType: 'Practice test',
        teaches: 'TOEFL Reading, Listening, Speaking, Writing',
        provider: {
          '@type': 'EducationalOrganization',
          name: 'Power TOEFL',
          url: SITE_URL,
        },
        isAccessibleForFree: true,
      });
    } else {
      const existing = document.getElementById(courseLdId);
      if (existing) existing.remove();
    }

  }, [effectiveTitle, effectiveDescription, path, resolvedOgImage, lang, localizedKeywords, tagline, contentType]);

  return null;
};

export default SEOHead;
